import { cn } from "@-/com.npmjs.cn";
import { Platform, View } from "@-/com.npmjs.react-native";
import type { ReactNode } from "react";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";

/**
 * The overlay lane for page actions on the left of native toolbars, above the row's pills
 * (or, on the list pages that have no opaque bar, above the list's reserved top band).
 *
 * Native controls reserve 9px above their glass; web controls stay centered in the row.
 */
export interface ToolbarActionsLaneProps {
  children: ReactNode;
  /** Extra classes merged onto the lane (e.g. a per-toolbar gap between sibling controls). */
  className?: string;
  /**
   * Defaults to `box-none` so the lane itself never swallows taps meant for the pills scrolling
   * beneath it — only its actual buttons are hit-testable.
   */
  pointerEvents?: "auto" | "box-none" | "box-only" | "none";
  /**
   * Reserve the app shell's trailing drawer lane for closed toolbar controls. Open menus use
   * a modal spanning the safe-area width, including the drawer. Web has no absolute app-shell
   * drawer trigger, so this inset is native-only, matching `ToolbarPillRow`.
   */
  reserveProfileTriggerLane?: boolean;
}

// Native-only, matching `ToolbarPillRow`: web's profile trigger lives in the header rather than in
// an absolute app-shell lane, so there is nothing to reserve space for.

export function ToolbarActionsLane({
  children,
  className,
  pointerEvents = "box-none",
  reserveProfileTriggerLane = true,
}: ToolbarActionsLaneProps) {
  const { actionsLeadingInset, edgeInset, height, trailingInset } = useToolbarGridMetrics();
  const right =
    Platform.OS === "web"
      ? reserveProfileTriggerLane
        ? 0
        : undefined
      : reserveProfileTriggerLane
        ? trailingInset
        : undefined;
  return (
    <View
      className={cn(
        "absolute top-0 z-20 flex-row",
        // The More trigger adds 2px of its own margin: 7px + 2px = 9px from the edge.
        Platform.OS === "web" ? "left-1 items-center" : "items-start justify-start pt-2.25",
        className,
      )}
      pointerEvents={pointerEvents}
      style={{
        height,
        left: Platform.OS !== "web" ? edgeInset - 2 + actionsLeadingInset : undefined,
        right,
      }}
    >
      {children}
    </View>
  );
}
